/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * CampaignIssuesView — admin cockpit for the tenant's campaign issue
 * picker list.
 *
 * The picker list powers the "top issues" multi-select on every Stage 3
 * Talked knock card. Pre-Slice 3 the only way to seed it was to SSH into
 * the backend and run raw SQL (or use the /docs Swagger UI), which was
 * a real operational hole — coordinators couldn't update their own
 * campaign messaging without a backend person on call. This view closes
 * that hole.
 *
 * Role walls (enforced at the backend):
 *
 *   - GET  /canvass/issues               any CANVASSER+ (read-only)
 *   - POST /canvass/issues               ISSUE_ADMIN_ROLES (ADMIN / ORG_MANAGER / MANAGER)
 *   - PATCH /canvass/issues/{id}         ISSUE_ADMIN_ROLES
 *
 * This view does NOT do a client-side role check before rendering the
 * create / edit / toggle controls. Instead it optimistically shows them
 * to everyone and lets the backend 403 surface as an inline red banner
 * via `formatCanvassError`. That's the same pattern as the Slice 2
 * Canvass views and keeps the front end from having to know the role
 * taxonomy. A canvasser who wanders in here will be able to read the
 * list but every mutation will 403 — which is the correct behavior,
 * just not a pretty one. If coordinators start filing tickets about
 * the noise we'll add a role-gated branch then.
 *
 * Sort order: always `(is_active DESC, display_order ASC, slug ASC)`
 * client-side so active issues float to the top even when display_order
 * is dense. The backend sorts by `(display_order, slug)` but that mixes
 * inactive rows in when `include_inactive=true`, which is confusing.
 *
 * Display order model: we present display_order as an integer input in
 * the edit panel rather than exposing drag-and-drop reordering. First
 * pass is an admin tool, not a dragbar. If coordinators ask for drag
 * reorder later it's a pure-client refactor — the PATCH endpoint is
 * already flexible enough.
 */
import React, { useEffect, useMemo, useState } from 'react';

import {
  createIssue,
  formatCanvassError,
  listIssues,
  updateIssue,
} from '@/api/canvass';
import { listCandidates } from '@/api/candidates';
import type {
  CampaignIssueCreate,
  CampaignIssueRead,
  CampaignIssueUpdate,
} from '@/types/canvass';
import { candidateShortName, type CandidateListItem } from '@/types/candidate';
import { InfoTip } from '@/components/InfoTip';
import { OnboardingModal, OnboardingHelpButton } from '@/components/OnboardingModal';

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function CampaignIssuesView({ onBack }: { onBack: () => void }) {
  const [includeInactive, setIncludeInactive] = useState(true);
  const [issues, setIssues] = useState<CampaignIssueRead[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [forceOnboard, setForceOnboard] = useState(false);
  // Owner picker source + badge resolution. Both slices (there is no "all"
  // mode over the wire — see api/candidates.ts) so an issue owned by a
  // deactivated candidate still renders a name, not a bare id. Swallowed on
  // failure: a canvasser reading this view may lack ANALYTICS_READ_ROLES,
  // and the issues list must still render — badges just degrade to "#id".
  const [candidates, setCandidates] = useState<CandidateListItem[]>([]);

  useEffect(() => {
    Promise.all([
      listCandidates(true).catch(() => [] as CandidateListItem[]),
      listCandidates(false).catch(() => [] as CandidateListItem[]),
    ]).then(([active, inactive]) => setCandidates([...active, ...inactive]));
  }, []);

  const candidateName = (id: number | null): string | null => {
    if (id === null) return null;
    const c = candidates.find((x) => x.id === id);
    return c ? candidateShortName(c) : `candidate #${id}`;
  };

  async function refresh() {
    setIssues(null);
    setError(null);
    try {
      const rows = await listIssues(includeInactive);
      setIssues(rows);
    } catch (e) {
      setError(formatCanvassError(e));
      setIssues([]);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  const sortedIssues = useMemo(() => {
    if (!issues) return [];
    return [...issues].sort((a, b) => {
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
      // Org-wide issues first, then grouped by owning candidate so each
      // campaign's list reads as a block.
      const aOrg = a.candidate_id === null;
      const bOrg = b.candidate_id === null;
      if (aOrg !== bOrg) return aOrg ? -1 : 1;
      if (!aOrg && !bOrg && a.candidate_id !== b.candidate_id)
        return (a.candidate_id as number) - (b.candidate_id as number);
      if (a.display_order !== b.display_order)
        return a.display_order - b.display_order;
      return a.slug.localeCompare(b.slug);
    });
  }, [issues]);

  async function handleToggleActive(issue: CampaignIssueRead) {
    setBusyId(issue.id);
    setError(null);
    try {
      await updateIssue(issue.id, { is_active: !issue.is_active });
      await refresh();
    } catch (e) {
      setError(formatCanvassError(e));
    } finally {
      setBusyId(null);
    }
  }

  async function handleSaveEdit(
    issue: CampaignIssueRead,
    patch: CampaignIssueUpdate,
  ) {
    setBusyId(issue.id);
    setError(null);
    try {
      await updateIssue(issue.id, patch);
      setEditingId(null);
      await refresh();
    } catch (e) {
      setError(formatCanvassError(e));
    } finally {
      setBusyId(null);
    }
  }

  async function handleCreate(body: CampaignIssueCreate): Promise<boolean> {
    setError(null);
    try {
      await createIssue(body);
      await refresh();
      return true;
    } catch (e) {
      setError(formatCanvassError(e));
      return false;
    }
  }

  return (
    <div className="space-y-4">
      <OnboardingModal
        viewKey="campaignissuesview"
        title="Campaign Issues"
        intro="Manage the picker list that powers the 'top issues' multi-select on every Talked knock card. Issues can be org-wide (shown for every candidate) or scoped to one candidate — a canvasser only sees a candidate's issues when that candidate is on the walk's slate. Only ADMIN / ORG_MANAGER / MANAGER can mutate — canvassers see this as a read-only preview."
        termIds={['campaign_issues', 'knock', 'briefing']}
        workflows={[
          'Add issues your canvassers should be asking about this week.',
          "Scope an issue to one candidate when it's theirs alone — leave it org-wide when every campaign shares it.",
          'Deactivate obsolete issues instead of deleting — historical knock data stays intact.',
          'Use display_order to push priority topics to the top of the picker.',
        ]}
        forceOpen={forceOnboard}
        onClose={() => setForceOnboard(false)}
      />

      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-fg-muted uppercase tracking-wide">
            Admin · canvass
          </div>
          <h1 className="text-3xl font-bold text-fg-primary mt-1 flex items-center gap-2">
            Campaign Issues <InfoTip termId="campaign_issues" />
          </h1>
          <p className="text-fg-muted text-sm mt-1">
            Picker list for Stage 3 Talked knocks. Edited by managers, used by every canvasser.
          </p>
        </div>
        <div className="flex gap-2">
          <OnboardingHelpButton onClick={() => setForceOnboard(true)} />
        </div>
      </div>

      {error && <ErrorBanner message={error} onClear={() => setError(null)} />}

      <CreateIssueForm onSubmit={handleCreate} candidates={candidates} />

      <div className="bg-bg-raised rounded-lg border border-border-base p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs text-fg-muted uppercase tracking-wide">
            {sortedIssues.length} issue{sortedIssues.length === 1 ? '' : 's'}
          </div>
          <div className="flex gap-2 items-center">
            <label className="text-xs text-fg-secondary flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                className="min-h-11 min-w-11 sm:min-h-0 sm:min-w-0"
              />
              Show inactive
            </label>
            <button
              onClick={refresh}
              className="px-3 py-1 rounded text-xs font-semibold bg-bg-elevated hover:bg-border-subtle text-fg-secondary inline-flex items-center justify-center min-h-11 sm:min-h-0"
            >
              ↻ Refresh
            </button>
          </div>
        </div>

        {issues === null && (
          <div className="text-fg-muted text-sm py-6 text-center">
            Loading issues…
          </div>
        )}
        {issues !== null && sortedIssues.length === 0 && (
          <div className="text-fg-muted text-sm py-6 text-center">
            No issues yet. Add your first one above — canvassers can't tag issues on their knocks until at least one is active.
          </div>
        )}
        {sortedIssues.length > 0 && (
          <div className="space-y-2">
            {sortedIssues.map((issue) =>
              editingId === issue.id ? (
                <EditIssueRow
                  key={issue.id}
                  issue={issue}
                  candidates={candidates}
                  busy={busyId === issue.id}
                  onSave={(patch) => handleSaveEdit(issue, patch)}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <IssueRow
                  key={issue.id}
                  issue={issue}
                  ownerName={candidateName(issue.candidate_id)}
                  busy={busyId === issue.id}
                  onEdit={() => setEditingId(issue.id)}
                  onToggleActive={() => handleToggleActive(issue)}
                />
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create form
// ---------------------------------------------------------------------------

function CreateIssueForm({
  onSubmit,
  candidates,
}: {
  onSubmit: (body: CampaignIssueCreate) => Promise<boolean>;
  candidates: CandidateListItem[];
}) {
  const [slug, setSlug] = useState('');
  const [label, setLabel] = useState('');
  const [displayOrder, setDisplayOrder] = useState<string>('100');
  // '' = org-wide (every candidate); otherwise a candidate id as a string.
  const [ownerId, setOwnerId] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const activeCandidates = candidates.filter((c) => c.is_active);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!slug.trim() || !label.trim()) return;
    setSaving(true);
    const ok = await onSubmit({
      slug: slug.trim().toLowerCase().replace(/\s+/g, '_'),
      label: label.trim(),
      is_active: true,
      display_order: Number.isFinite(parseInt(displayOrder, 10))
        ? parseInt(displayOrder, 10)
        : 100,
      candidate_id: ownerId === '' ? null : parseInt(ownerId, 10),
    });
    setSaving(false);
    if (ok) {
      setSlug('');
      setLabel('');
      setDisplayOrder('100');
      setOwnerId('');
    }
  }

  return (
    <form
      onSubmit={submit}
      className="bg-bg-raised rounded-lg border border-border-base p-4 space-y-2"
    >
      <div className="text-xs text-fg-muted uppercase tracking-wide">
        Add a new issue
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
        <div className="sm:col-span-2">
          <div className="text-[10px] text-fg-muted uppercase mb-1">
            Slug (url-safe)
          </div>
          <input
            className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
            placeholder="e.g. public_safety"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            disabled={saving}
          />
        </div>
        <div className="sm:col-span-3">
          <div className="text-[10px] text-fg-muted uppercase mb-1">
            Label (shown to canvassers)
          </div>
          <input
            className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
            placeholder="e.g. Public Safety"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            disabled={saving}
          />
        </div>
        <div className="sm:col-span-1">
          <label htmlFor="campaign-issue-new-order" className="block text-[10px] text-fg-muted uppercase mb-1">
            Order
          </label>
          <input
            id="campaign-issue-new-order"
            type="number"
            className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
            disabled={saving}
          />
        </div>
        <div className="sm:col-span-6">
          <label htmlFor="campaign-issue-new-owner" className="block text-[10px] text-fg-muted uppercase mb-1">
            Candidate
          </label>
          <select
            id="campaign-issue-new-owner"
            className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
            disabled={saving}
          >
            <option value="">All candidates (org-wide)</option>
            {activeCandidates.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {candidateShortName(c)} — {c.office}
              </option>
            ))}
          </select>
        </div>
      </div>
      <button
        type="submit"
        disabled={saving || !slug.trim() || !label.trim()}
        className="bg-accent hover:bg-accent-hover disabled:opacity-40 text-white font-bold px-4 py-2 rounded text-sm min-h-11 sm:min-h-0"
      >
        {saving ? 'Saving…' : '+ Add issue'}
      </button>
      <div className="text-[11px] text-fg-muted">
        Slugs auto-lowercase and spaces become underscores. Slug must be unique in the tenant.
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Row display / edit
// ---------------------------------------------------------------------------

function IssueRow({
  issue,
  ownerName,
  busy,
  onEdit,
  onToggleActive,
}: {
  issue: CampaignIssueRead;
  /** Resolved owner name for candidate-scoped issues; null = org-wide. */
  ownerName: string | null;
  busy: boolean;
  onEdit: () => void;
  onToggleActive: () => void;
}) {
  return (
    <div
      className={`bg-bg-base rounded border px-3 py-3 flex items-center justify-between gap-3 ${
        issue.is_active ? 'border-border-base' : 'border-border-base opacity-60'
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-fg-primary truncate">{issue.label}</span>
          <span className="text-[10px] text-fg-secondary bg-bg-raised border border-border-base rounded px-1.5 py-0.5 shrink-0">
            {ownerName ?? 'Org-wide'}
          </span>
          {!issue.is_active && (
            <span className="text-[10px] text-fg-muted bg-bg-raised border border-border-base rounded px-1.5 py-0.5">
              INACTIVE
            </span>
          )}
        </div>
        <div className="text-xs text-fg-muted mt-0.5 font-mono">
          {issue.slug} · order {issue.display_order} · created {formatTime(issue.created_at)}
        </div>
      </div>
      <div className="flex gap-2 items-center shrink-0">
        <button
          onClick={onToggleActive}
          disabled={busy}
          className={`text-xs font-semibold px-3 py-1 rounded ${
            issue.is_active
              ? 'bg-bg-elevated hover:bg-border-subtle text-fg-secondary'
              : 'bg-status-success-bg hover:bg-status-success-bg/80 text-white'
          } disabled:opacity-40`}
        >
          {issue.is_active ? 'Deactivate' : 'Reactivate'}
        </button>
        <button
          onClick={onEdit}
          disabled={busy}
          className="text-xs font-semibold px-3 py-1 rounded bg-bg-elevated hover:bg-border-subtle text-fg-secondary disabled:opacity-40"
        >
          Edit
        </button>
      </div>
    </div>
  );
}

function EditIssueRow({
  issue,
  candidates,
  busy,
  onSave,
  onCancel,
}: {
  issue: CampaignIssueRead;
  candidates: CandidateListItem[];
  busy: boolean;
  onSave: (patch: CampaignIssueUpdate) => void;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState(issue.label);
  const [order, setOrder] = useState<string>(String(issue.display_order));
  // '' = org-wide. PATCH semantics: candidate_id goes in the payload only
  // when it changed — an explicit null clears to org-wide server-side.
  const [ownerId, setOwnerId] = useState<string>(
    issue.candidate_id === null ? '' : String(issue.candidate_id),
  );

  // Active candidates, plus the current owner even if deactivated so the
  // select doesn't silently show the wrong owner.
  const ownerOptions = candidates.filter(
    (c) => c.is_active || c.id === issue.candidate_id,
  );

  return (
    <div className="bg-bg-base rounded border border-accent px-3 py-3 space-y-2">
      <div className="text-xs text-fg-muted uppercase tracking-wide font-mono">
        editing {issue.slug}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
        <div className="sm:col-span-5">
          <div className="text-[10px] text-fg-muted uppercase mb-1">Label</div>
          <input
            className="w-full bg-bg-raised border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="sm:col-span-1">
          <label htmlFor={`campaign-issue-${issue.id}-order`} className="block text-[10px] text-fg-muted uppercase mb-1">Order</label>
          <input
            id={`campaign-issue-${issue.id}-order`}
            type="number"
            className="w-full bg-bg-raised border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
            value={order}
            onChange={(e) => setOrder(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="sm:col-span-6">
          <label htmlFor={`campaign-issue-${issue.id}-owner`} className="block text-[10px] text-fg-muted uppercase mb-1">Candidate</label>
          <select
            id={`campaign-issue-${issue.id}-owner`}
            className="w-full bg-bg-raised border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
            disabled={busy}
          >
            <option value="">All candidates (org-wide)</option>
            {ownerOptions.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {candidateShortName(c)} — {c.office}
                {c.is_active ? '' : ' (inactive)'}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          disabled={busy || !label.trim()}
          onClick={() => {
            const nextOwner = ownerId === '' ? null : parseInt(ownerId, 10);
            const patch: CampaignIssueUpdate = {
              label: label.trim(),
              display_order: Number.isFinite(parseInt(order, 10))
                ? parseInt(order, 10)
                : issue.display_order,
            };
            if (nextOwner !== issue.candidate_id) patch.candidate_id = nextOwner;
            onSave(patch);
          }}
          className="bg-accent hover:bg-accent-hover disabled:opacity-40 text-white font-bold px-4 py-2 rounded text-sm"
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button
          onClick={onCancel}
          disabled={busy}
          className="bg-bg-elevated hover:bg-border-subtle disabled:opacity-40 text-fg-secondary font-semibold px-4 py-2 rounded text-sm"
        >
          Cancel
        </button>
      </div>
      <div className="text-[11px] text-fg-muted">
        Slug is immutable — deactivate and create a new issue if you need to rename the identifier.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error banner (same pattern as CanvassReviewView)
// ---------------------------------------------------------------------------

function ErrorBanner({
  message,
  onClear,
}: {
  message: string;
  onClear: () => void;
}) {
  return (
    <div className="bg-status-danger-bg/40 border border-status-danger-bg rounded p-3 flex items-start justify-between gap-3">
      <div className="text-sm text-status-danger">{message}</div>
      <button
        onClick={onClear}
        className="text-status-danger hover:text-fg-primary text-xs"
      >
        dismiss
      </button>
    </div>
  );
}

export default CampaignIssuesView;
